import React, { useMemo, useRef, useState } from 'react';
import pluralize from 'pluralize';
import { useUi, useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, CdnMedia, RoundPlayButton } from '@/components';
import { shuffleArray } from '@/utils/array';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';

import { MultipleChoiceGame } from '../components/MultipleChoiceGame';

interface ReadingSingularOrPluralStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
  singulars: Array<string>;
  plurals: Array<string>;
}

const ReadingSingularOrPluralStep = ({
  activity,
  mode,
  isPreview,
  wrapperClassName,
  index,
  total,
  word,
  singulars,
}: ReadingSingularOrPluralStepProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  const [selectedChoices, setSelectedChoices] = useState<Array<string>>([]);

  const singular = useMemo(
    () => (singulars.includes(word) ? word : pluralize.singular(word)),
    [singulars, word],
  );

  const plural = useMemo(
    () => (singulars.includes(word) ? pluralize.plural(word) : word),
    [singulars, word],
  );

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      showTitle={true}
      showActivityPagination={false}
    >
      <div className="flex flex-col items-center justify-center gap-6">
        <RoundPlayButton
          role="button"
          ariaLabel="Play sound"
          size="sm"
          color="white"
          onClick={() => setPlayedOnce(true)}
          pressHint={!playedOnce}
          disabled={!playerReady}
        />

        <CdnMedia
          className="block mx-auto"
          symbol={word}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />

        <MultipleChoiceGame
          choices={[singular, plural]}
          correctChoice={singulars.includes(word) ? singular : plural}
          selectedChoices={selectedChoices}
          setSelectedChoices={setSelectedChoices}
          useGrid={false}
          squareChoices={false}
        />

        <AudioPlayer
          ref={playerRef}
          src={mediaUrl || ''}
          onReadySrc={() => setPlayerReady(true)}
          onLoadTimeoutSrc={() =>
            sendAlert('We could not load some of the audio for this lesson.', {
              type: 'warning',
              title: 'There was a problem',
            })
          }
        />
      </div>
    </ActivityStep>
  );
};

export const ReadingSingularOrPlural = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words || !activity.words2) {
    throw new Error('Words and words 2 are required for this activity');
  }

  const words = useMemo(
    () =>
      shuffleArray([
        ...(activity.words ? activity.words : []),
        ...(activity.words2 ? activity.words2 : []),
      ]),
    [activity.words, activity.words2],
  );

  const TOTAL_STEPS = 1 + words.length;

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          isPreview={isPreview}
          index={0}
          total={TOTAL_STEPS}
        />,
        ...words.flatMap((word, index) => [
          <ReadingSingularOrPluralStep
            key={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={TOTAL_STEPS}
            word={word}
            singulars={activity.words || []}
            plurals={activity.words2 || []}
          />,
        ]),
      ]}
    />
  );
};
